/**
 * Utility functions for formatting currencies, numbers, and generating IDs
 */

/**
 * Format currency amount with proper localization
 */
export function formatCurrency(
  amount: number,
  currencyCode: string,
  precision: number = 2,
  locale: string = 'en-US'
): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    }).format(amount)
  } catch (error) {
    // Fallback formatting if currency is not supported
    return `${getCurrencySymbol(currencyCode)}${formatNumber(amount, precision)}`
  }
}

/**
 * Format number with proper localization and precision
 */
export function formatNumber(
  value: number,
  precision: number = 2,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value)
}

/**
 * Format percentage with proper localization
 */
export function formatPercentage(
  value: number,
  precision: number = 2,
  locale: string = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  }).format(value / 100)
}

/**
 * Get currency symbol for a given currency code
 */
export function getCurrencySymbol(currencyCode: string): string {
  const symbols: Record<string, string> = {
    // Major currencies
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'Fr',
    CNY: '¥',
    SEK: 'kr',
    NOK: 'kr',
    DKK: 'kr',
    PLN: 'zł',
    CZK: 'Kč',
    HUF: 'Ft',
    RUB: '₽',
    INR: '₹',
    BRL: 'R$',
    MXN: '$',
    ZAR: 'R',
    KRW: '₩',
    SGD: '$',
    HKD: '$',
    THB: '฿',
    TRY: '₺',
    ILS: '₪',
    AED: 'د.إ',
    SAR: '﷼',
    
    // Cryptocurrencies
    BTC: '₿',
    ETH: 'Ξ',
    LTC: 'Ł',
    XRP: 'XRP',
    ADA: '₳',
    DOT: '●',
    
    // Precious metals
    XAU: 'Au',
    XAG: 'Ag',
    XPT: 'Pt',
    XPD: 'Pd',
  }

  return symbols[currencyCode] || currencyCode
}

/**
 * Generate unique ID for alerts, transactions, etc.
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

/**
 * Calculate median value from array of numbers
 */
export function calculateMedian(numbers: number[]): number {
  if (numbers.length === 0) return 0
  
  const sorted = [...numbers].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2
  }
  
  return sorted[middle]
}

/**
 * Calculate standard deviation
 */
export function calculateStandardDeviation(numbers: number[]): number {
  if (numbers.length === 0) return 0
  
  const mean = numbers.reduce((sum, num) => sum + num, 0) / numbers.length
  const squaredDifferences = numbers.map(num => Math.pow(num - mean, 2))
  const variance = squaredDifferences.reduce((sum, diff) => sum + diff, 0) / numbers.length
  
  return Math.sqrt(variance)
}

/**
 * Format large numbers with compact notation (K, M, B)
 */
export function formatCompactNumber(
  value: number,
  precision: number = 1,
  locale: string = 'en-US'
): string {
  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 0,
      maximumFractionDigits: precision,
    }).format(value)
  } catch (error) {
    // Fallback for browsers that don't support compact notation
    if (Math.abs(value) >= 1e9) {
      return `${(value / 1e9).toFixed(precision)}B`
    } else if (Math.abs(value) >= 1e6) {
      return `${(value / 1e6).toFixed(precision)}M`
    } else if (Math.abs(value) >= 1e3) {
      return `${(value / 1e3).toFixed(precision)}K`
    }
    return value.toFixed(precision)
  }
}

/**
 * Parse currency string to number (remove symbols and formatting)
 */
export function parseCurrencyString(currencyString: string): number {
  // Remove currency symbols, spaces, and common formatting
  const cleanString = currencyString
    .replace(/[€$£¥₹₽¢₩₪₨₦₡₵₸₴₱₲₫₵₼₽]/g, '')
    .replace(/[^\d.,\-+]/g, '')
    .replace(/,(?=\d{3})/g, '') // Remove thousands separators
    .trim()
  
  return parseFloat(cleanString) || 0
}

/**
 * Format time difference in human readable format
 */
export function formatTimeAgo(date: Date): string {
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  
  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60)
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600)
    return `${hours} hour${hours > 1 ? 's' : ''} ago`
  } else if (diffInSeconds < 2592000) {
    const days = Math.floor(diffInSeconds / 86400)
    return `${days} day${days > 1 ? 's' : ''} ago`
  } else {
    return date.toLocaleDateString()
  }
}

/**
 * Round to specific number of decimal places
 */
export function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

/**
 * Check if a number is within a percentage range of another number
 */
export function isWithinPercentage(
  value: number,
  target: number,
  percentage: number
): boolean {
  const tolerance = Math.abs(target * (percentage / 100))
  return Math.abs(value - target) <= tolerance
}

/**
 * Convert currency pair string to array
 */
export function parseCurrencyPair(pair: string): [string, string] {
  const parts = pair.split('-')
  if (parts.length !== 2) {
    throw new Error(`Invalid currency pair format: ${pair}`)
  }
  return [parts[0], parts[1]]
}

/**
 * Create currency pair string from two currencies
 */
export function createCurrencyPair(from: string, to: string): string {
  return `${from}-${to}`
}

/**
 * Validate currency code format (3 letters)
 */
export function isValidCurrencyCode(code: string): boolean {
  return /^[A-Z]{3}$/.test(code)
}

/**
 * Sort currencies by popularity/usage
 */
export function sortCurrenciesByPopularity(currencies: string[]): string[] {
  const popularityOrder = [
    'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'SEK', 'NOK',
    'DKK', 'PLN', 'CZK', 'HUF', 'RUB', 'INR', 'BRL', 'MXN', 'ZAR', 'KRW',
    'SGD', 'HKD', 'THB', 'TRY', 'ILS', 'AED', 'SAR'
  ]
  
  return currencies.sort((a, b) => {
    const indexA = popularityOrder.indexOf(a)
    const indexB = popularityOrder.indexOf(b)
    
    if (indexA === -1 && indexB === -1) return a.localeCompare(b)
    if (indexA === -1) return 1
    if (indexB === -1) return -1
    
    return indexA - indexB
  })
}

/**
 * Format date for API requests
 */
export function formatDateForAPI(date: Date): string {
  return date.toISOString().split('T')[0]
}

/**
 * Parse date from API response
 */
export function parseDateFromAPI(dateString: string): Date {
  return new Date(dateString)
}

/**
 * Generate color for charts based on currency code
 */
export function getCurrencyColor(currencyCode: string): string {
  const colors: Record<string, string> = {
    USD: '#22c55e', // Green
    EUR: '#3b82f6', // Blue
    GBP: '#ef4444', // Red
    JPY: '#f59e0b', // Amber
    CAD: '#ef4444', // Red
    AUD: '#10b981', // Emerald
    CHF: '#8b5cf6', // Violet
    CNY: '#ef4444', // Red
    BTC: '#f59e0b', // Orange
    ETH: '#6366f1', // Indigo
  }
  
  return colors[currencyCode] || `#${Math.floor(Math.random()*16777215).toString(16)}`
}